import { ALL_TECHS, Cat, KINDS } from '../../../gameplay/registry';
import { AGE_UP_REQUIREMENTS } from '../../../gameplay/technologies';
import { AGE_NAMES, C } from '../../../gameplay/types';
import { FX_ONE } from '../fixed';
import { Order, UState, type PlayerState, type World } from '../world';
import { gatherableBy, orderGather, resourceOf } from './economy';
import { orderBuild } from './construction';
import { setDestination } from './movement';

export const MAX_QUEUE = 15;

export function canAfford(p: PlayerState, cost: number[]): boolean {
  for (let r = 0; r < 4; r++) if (p.res[r] < cost[r] * FX_ONE) return false;
  return true;
}
export function pay(p: PlayerState, cost: number[]): void {
  for (let r = 0; r < 4; r++) p.res[r] -= cost[r] * FX_ONE;
}
export function refund(p: PlayerState, cost: number[]): void {
  for (let r = 0; r < 4; r++) p.res[r] += cost[r] * FX_ONE;
}

/** Why a unit cannot be trained, or null if it can (ignoring resources). */
export function unitAvailability(w: World, p: PlayerState, building: number, unitKind: number): string | null {
  const k = KINDS[unitKind];
  if (!k.unit) return 'Not a unit';
  if (k.unit.trainedAt !== KINDS[w.kind[building]].key) return 'Not trained here';
  if (!p.stats[unitKind].enabled) return 'Unavailable to your civilisation';
  if (p.age < k.unit.age) return `Requires ${AGE_NAMES[k.unit.age]}`;
  return null;
}

/** Units trainable at a building for a player (including civ uniques). */
export function trainableAt(w: World, p: PlayerState, building: number): number[] {
  const key = KINDS[w.kind[building]].key;
  const out: number[] = [];
  for (const k of KINDS) {
    if (!k.unit || k.unit.trainedAt !== key) continue;
    if (!p.stats[k.index].enabled) continue;
    out.push(k.index);
  }
  return out;
}

/** Technologies researchable at a building for a player (excluding already researched). */
export function researchableAt(w: World, p: PlayerState, building: number): number[] {
  const key = KINDS[w.kind[building]].key;
  const out: number[] = [];
  for (let i = 0; i < ALL_TECHS.length; i++) {
    const t = ALL_TECHS[i];
    if (t.researchedAt !== key || p.researched[i] || !p.techStats[i].enabled) continue;
    if (t.advancesTo !== undefined && t.advancesTo !== p.age + 1) continue;
    out.push(i);
  }
  return out;
}

export function techAvailability(w: World, p: PlayerState, tech: number): string | null {
  const t = ALL_TECHS[tech];
  if (p.researched[tech]) return 'Already researched';
  if (p.researching[tech]) return 'In progress';
  if (!p.techStats[tech].enabled) return 'Unavailable to your civilisation';
  if (t.advancesTo !== undefined) {
    if (p.age !== t.age) return 'Requires previous age';
    const req = AGE_UP_REQUIREMENTS[t.advancesTo];
    let have = 0;
    for (const key of req.from) {
      const ki = KINDS.findIndex((k) => k.key === key);
      if (ki >= 0 && p.buildingCounts[ki] > 0) have++;
    }
    if (have < req.count) return `Build ${req.count - have} more of: ${req.from.map((k) => KINDS.find((x) => x.key === k)?.building?.name ?? k).join(', ')}`;
    // Only one age-up at a time.
    for (let i = 0; i < ALL_TECHS.length; i++) if (p.researching[i] && ALL_TECHS[i].advancesTo !== undefined) return 'Already advancing';
  } else if (p.age < t.age) return 'Requires a later age';
  for (const r of t.requires ?? []) {
    const ri = ALL_TECHS.findIndex((x) => x.key === r);
    if (!p.researched[ri]) return `Requires ${ALL_TECHS[ri].name}`;
  }
  void w;
  return null;
}

function spawnTrained(w: World, b: number, unitKind: number): void {
  const owner = w.owner[b];
  const rt = w.resolve(w.rallyTarget[b]);
  const rx = rt >= 0 ? w.x[rt] : w.rallyX[b];
  const ry = rt >= 0 ? w.y[rt] : w.rallyY[b];
  const naval = (KINDS[unitKind].unit!.classes & C.Ship) !== 0;
  const [ex, ey] = w.exitPoint(b, rx, ry, naval);
  const u = w.spawn(unitKind, owner, ex, ey);
  if (u < 0) return;
  w.events.push({ e: 'spawn', id: w.idOf(u), kind: unitKind, owner });
  const isVillager = KINDS[unitKind].unit!.classes & 1 || (naval && KINDS[unitKind].unit!.gatherRates);
  if (rt >= 0 && isVillager && resourceOf(w, rt) >= 0 && gatherableBy(w, u, rt)) {
    orderGather(w, u, rt);
    return;
  }
  if (rt >= 0 && !naval && isVillager && w.cat[rt] === Cat.Building && w.owner[rt] === owner && !w.built[rt]) {
    orderBuild(w, u, rt);
    return;
  }
  if (rx !== w.x[b] || ry !== w.y[b]) {
    w.order[u] = Order.Move;
    w.state[u] = UState.Walking;
    w.goalX[u] = rx;
    w.goalY[u] = ry;
    // Soldiers on their way to the rally point fight back if attacked (see combat.ts).
    w.rallying[u] = isVillager ? 0 : 1;
    setDestination(w, u, rx, ry);
  }
}

export function updateProduction(w: World): void {
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    if (!w.alive[s] || w.cat[s] !== Cat.Building || !w.built[s]) continue;
    const q = w.queues[s];
    if (!q || q.length === 0) continue;
    const p = w.players[w.owner[s]];
    const item = q[0];
    if (item.kind >= 0) {
      const pop = KINDS[item.kind].unit!.pop;
      if (item.progress === 0 && p.pop + pop > p.popCap) {
        if (w.tick % 100 === 0) w.events.push({ e: 'popCapped', owner: p.id });
        continue;
      }
    }
    item.progress++;
    if (item.progress < item.total) continue;
    q.shift();
    if (item.kind >= 0) {
      spawnTrained(w, s, item.kind);
      p.pop += KINDS[item.kind].unit!.pop;
    } else {
      completeResearch(w, p, item.tech);
    }
  }
}

export function completeResearch(w: World, p: PlayerState, tech: number): void {
  p.researching[tech] = 0;
  p.researched[tech] = 1;
  const t = ALL_TECHS[tech];
  if (t.advancesTo !== undefined) {
    p.age = Math.max(p.age, t.advancesTo);
    w.events.push({ e: 'age', owner: p.id, age: p.age });
  }
  w.recomputeStats(p);
  w.events.push({ e: 'research', tech, owner: p.id });
}

/** Recount population and population cap. */
export function updatePopulation(w: World): void {
  for (const p of w.players) {
    p.pop = 0;
    p.popCap = 0;
  }
  const n = w.highSlot;
  for (let s = 0; s < n; s++) {
    if (!w.alive[s]) continue;
    const o = w.owner[s];
    if (o < 0) continue;
    const k = KINDS[w.kind[s]];
    if (k.unit) w.players[o].pop += k.unit.pop;
    else if (k.building && w.built[s]) w.players[o].popCap += k.building.pop;
  }
  for (const p of w.players) p.popCap = Math.min(200, p.popCap);
}
